
// src/pages/CommunityPanel.jsx
import React, { useMemo, useRef, useState } from "react";
import { Nav } from "react-bootstrap";
import AddCommunity from "./AddCommunity";
import ViewCommunities from "./ViewCommunities";
import RequestsList from "./RequestsList";

const ADD_TAB = "add";
const VIEW_TAB = "view";
const REQ_TAB = "requests";

const INNER_TABS = [
    { key: ADD_TAB, label: "Add Community", icon: "bi-plus-circle" },
    { key: VIEW_TAB, label: "View Communities", icon: "bi-card-checklist" },
    { key: REQ_TAB, label: "Community Requests", icon: "bi-inbox" },
];

export default function CommunityPanel() {
    const [innerTab, setInnerTab] = useState(VIEW_TAB);

    const renderInner = useMemo(() => {
        switch (innerTab) {
            case ADD_TAB:
                return <AddCommunity />;
            case VIEW_TAB:
                return <ViewCommunities />;
            case REQ_TAB:
                return <RequestsList />;
            default:
                return <ViewCommunities />;
        }
    }, [innerTab]);

    /* Optional: inner swipe (left/right) */
    const touchStartRef = useRef({ x: 0, y: 0 });
    const touchLastRef = useRef({ x: 0, y: 0 });
    const interactiveSelector = "input, textarea, select, button, a, .btn, [role='button']";

    const onTouchStart = (e) => {
        const t = e.changedTouches?.[0];
        if (!t) return;
        if (e.target?.closest?.(interactiveSelector)) return;
        // Stop bubbling to outer dashboard to avoid switching outer tabs
        e.stopPropagation();
        touchStartRef.current = { x: t.clientX, y: t.clientY };
        touchLastRef.current = { x: t.clientX, y: t.clientY };
    };

    const onTouchMove = (e) => {
        const t = e.changedTouches?.[0];
        if (!t) return;
        e.stopPropagation();
        touchLastRef.current = { x: t.clientX, y: t.clientY };
    };

    const onTouchEnd = (e) => {
        e.stopPropagation();
        const { x: x0, y: y0 } = touchStartRef.current;
        const { x: x1, y: y1 } = touchLastRef.current;
        const dx = x1 - x0;
        const dy = y1 - y0;
        const absDx = Math.abs(dx);
        const absDy = Math.abs(dy);
        const H = 50, V = 30;

        if (absDx >= H && absDy <= V) {
            const idx = INNER_TABS.findIndex((t) => t.key === innerTab);
            if (idx === -1) return;
            if (dx < 0) {
                const next = Math.min(idx + 1, INNER_TABS.length - 1);
                setInnerTab(INNER_TABS[next].key);
            } else {
                const prev = Math.max(idx - 1, 0);
                setInnerTab(INNER_TABS[prev].key);
            }
            if (navigator.vibrate) navigator.vibrate(10);
        }
    };

    return (
        <section>
            {/* ---------- Mobile inner tabs (< md) ---------- */}
            <div
                className="d-block d-md-none bg-light border-bottom"
                style={{ position: "sticky", top: 0, zIndex: 15 }}
            >
                <div className="px-3 py-2 d-flex align-items-center justify-content-between">
                    <h6 className="mb-0">Community</h6>
                    <span className="text-muted small">{INNER_TABS.find(t => t.key === innerTab)?.label}</span>
                </div>
                <Nav
                    variant="pills"
                    className="px-2 pb-2 flex-nowrap"
                    style={{ overflowX: "auto", gap: "0.5rem" }}
                >
                    {INNER_TABS.map((t) => (
                        <Nav.Item key={t.key}>
                            <Nav.Link
                                onClick={() => setInnerTab(t.key)}
                                active={innerTab === t.key}
                                className="text-nowrap d-flex align-items-center gap-2"
                            >
                                <i className={`bi ${t.icon}`} aria-hidden="true" />
                                <span>{t.label}</span>
                            </Nav.Link>
                        </Nav.Item>
                    ))}
                </Nav>
            </div>


            {/* ---------- Desktop inner tabs (>= md) ---------- */}
            <div className="d-none d-md-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">Community</h4>
                <div className="btn-group">
                    {INNER_TABS.map((t) => (
                        <button
                            key={t.key}
                            className={`btn btn-sm ${innerTab === t.key ? "btn-primary" : "btn-outline-primary"} d-flex align-items-center gap-2`}
                            onClick={() => setInnerTab(t.key)}
                        >
                            <i className={`bi ${t.icon}`} aria-hidden="true" />
                            <span className="">{t.label}</span>
                        </button>
                    ))}
                </div>
            </div>
            <hr />

            {/* ---------- Inner Content (with inner swipe handlers) ---------- */}
            <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
                {renderInner}
            </div>
        </section>
    );
}
